import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, Code2, HelpCircle, Linkedin, Mail, ShieldCheck, Store, Truck, MessageSquare, Send, CheckCircle2, AlertCircle, PlusCircle, User, LogIn, Clock } from 'lucide-react';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import API from '../utils/api';
import { toast } from 'react-toastify';

const ISSUE_OPTIONS = [
  'Order Related',
  'Payment / Refund Issue',
  'Shop Registration & Verification',
  'Material & Stock Discrepancy',
  'Delivery & Location Problem',
  'General Inquiry'
];

const pages = {
  about: {
    title: 'About Material Stock',
    icon: Store,
    intro: 'Material Stock connects customers with nearby construction material shops for sand, cement, steel, bricks, and other essentials.',
    points: [
      'Customers can compare nearby shops and add materials to cart.',
      'Owners can list materials, manage stock, and receive orders.',
      'Admins can manage platform settings, referral rewards, branding, and revenue options.',
    ],
  },
  contact: {
    title: 'Contact Support',
    icon: Mail,
    intro: 'Need help with orders, payments, shop onboarding, or account access? Reach the Material Stock support team.',
    points: [
      'Address: Cyber Residency, Indira Nagar, Gachibowli, Hyderabad',
      'Phone: +91 6300440316',
      'Email: support@materialstock.local',
      'Support hours: 9:00 AM to 7:00 PM, Monday to Saturday',
    ],
  },
  help: {
    title: 'Help Center',
    icon: HelpCircle,
    intro: 'Quick answers for customers and shop owners using Material Stock.',
    points: [
      'Customers pay the platform or advance fee online and pay material balance as configured.',
      'Use shop directions on the shop detail page to find nearby stores.',
      'Referral rewards are shown in your dashboard when the admin enables the program.',
      'Owners can add materials, variants, offers, stock, and delivery details from their dashboard.',
    ],
  },
  privacy: {
    title: 'Privacy Policy',
    icon: ShieldCheck,
    intro: 'Material Stock uses account, order, location, and payment information only to run the marketplace experience.',
    points: [
      'Location data helps show nearby shops and directions.',
      'Payment data is processed through Razorpay and is not stored as card details in Material Stock.',
      'Referral and wallet activity is stored for reward tracking.',
      'Users can contact support for account or data questions.',
    ],
  },
  terms: {
    title: 'Terms & Conditions',
    icon: Truck,
    intro: 'By using Material Stock, customers, owners, and admins agree to use the platform responsibly.',
    points: [
      'Shop owners are responsible for accurate prices, stock, delivery information, and order fulfillment.',
      'Customers should verify quantities, address, and payment details before placing orders.',
      'Platform fees, referral amounts, and revenue settings are controlled by the admin settings.',
      'Material Stock can update platform rules to improve safety, payments, and marketplace quality.',
    ],
  },
};

function SupportChatSection() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const ticketParam = searchParams.get('ticket');

  const [showChatBox, setShowChatBox] = useState(Boolean(ticketParam));
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [loadingChats, setLoadingChats] = useState(false);

  // New ticket form
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [issue, setIssue] = useState(ISSUE_OPTIONS[0]);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reply message
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  const messagesContainerRef = useRef(null);

  useEffect(() => {
    if (user && (showChatBox || ticketParam)) {
      if (ticketParam) setShowChatBox(true);
      fetchMyChats();
    }
  }, [user, showChatBox, ticketParam]);

  // Polling active chat
  useEffect(() => {
    let interval;
    if (showChatBox && activeChat && user) {
      interval = setInterval(() => {
        fetchMyChats(true);
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [showChatBox, activeChat, user]);

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [activeChat?.messages]);

  const fetchMyChats = async (isBackground = false) => {
    if (!isBackground) setLoadingChats(true);
    try {
      const res = await API.get('/support/my-chats');
      setChats(res.data);

      if (ticketParam) {
        const target = res.data.find(c => c._id === ticketParam);
        if (target) {
          setActiveChat(target);
          setIsCreatingNew(false);
        } else if (res.data.length > 0 && !activeChat && !isCreatingNew) {
          setActiveChat(res.data[0]);
        }
      } else if (activeChat) {
        const updated = res.data.find(c => c._id === activeChat._id);
        if (updated) setActiveChat(updated);
      } else if (res.data.length > 0 && !isCreatingNew) {
        setActiveChat(res.data[0]);
      }
    } catch (err) {
      if (!isBackground) toast.error('Failed to load support chats');
    } finally {
      if (!isBackground) setLoadingChats(false);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!description.trim()) {
      toast.error('Please enter a description for your issue');
      return;
    }

    setSubmitting(true);
    try {
      const res = await API.post('/support/chats', {
        orderId: orderId.trim(),
        issue,
        description: description.trim()
      });
      toast.success('Support ticket created successfully!');
      setOrderId('');
      setDescription('');
      setIsCreatingNew(false);
      await fetchMyChats();
      setActiveChat(res.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create support ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !activeChat) return;

    setSendingReply(true);
    try {
      const res = await API.post(`/support/chats/${activeChat._id}/messages`, {
        message: replyText.trim()
      });
      setReplyText('');
      setActiveChat(res.data);
      setChats(prev => prev.map(c => c._id === res.data._id ? res.data : c));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send message');
    } finally {
      setSendingReply(false);
    }
  };

  const handleResolveTicket = async () => {
    if (!activeChat) return;
    try {
      const res = await API.put(`/support/chats/${activeChat._id}/resolve`);
      toast.success('Ticket marked as resolved');
      setActiveChat(res.data);
      setChats(prev => prev.map(c => c._id === res.data._id ? res.data : c));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to resolve ticket');
    }
  };

  if (!user) {
    return (
      <section style={{ background: 'white', borderRadius: 14, padding: 24, boxShadow: 'var(--shadow)', marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--orange-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <MessageSquare size={22} color="var(--orange)" />
          </div>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 900 }}>Support Chat</h2>
            <p style={{ color: 'var(--gray)', fontSize: 13 }}>Live assistance for your orders and account</p>
          </div>
        </div>
        <div style={{ background: 'var(--gray-light)', borderRadius: 12, padding: 18, textAlign: 'center' }}>
          <p style={{ fontSize: 14, color: 'var(--dark)', marginBottom: 14 }}>
            Please log in to your account to start a live support chat with our team.
          </p>
          <Link to="/login" className="btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 20px' }}>
            <LogIn size={16} /> Log In to Start Support Chat
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section style={{ background: 'white', borderRadius: 14, padding: 24, boxShadow: 'var(--shadow)', marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--orange-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <MessageSquare size={22} color="var(--orange)" />
          </div>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 900 }}>Support Chat Options</h2>
            <p style={{ color: 'var(--gray)', fontSize: 13 }}>Type your order ID, issue, and description to get help</p>
          </div>
        </div>

        <button
          onClick={() => {
            setShowChatBox(!showChatBox);
            if (!showChatBox && chats.length === 0) fetchMyChats();
          }}
          className="btn-primary"
          style={{ padding: '9px 18px', display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <MessageSquare size={16} />
          {showChatBox ? 'Hide Chat Box' : 'Open Support Chat'}
        </button>
      </div>

      {showChatBox && (
        <div style={{ border: '1px solid var(--gray-border)', borderRadius: 12, overflow: 'hidden', marginTop: 14 }}>
          {loadingChats ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="spinner" /></div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', minHeight: 440 }}>
              {/* Left Column: Tickets list & create button */}
              <div style={{ borderRight: '1px solid var(--gray-border)', padding: 16, background: 'var(--gray-light)', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <span style={{ fontWeight: 800, fontSize: 14, color: 'var(--dark)' }}>Your Support Tickets</span>
                  <button
                    onClick={() => { setIsCreatingNew(true); setActiveChat(null); }}
                    style={{ background: 'var(--orange)', color: 'white', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                  >
                    <PlusCircle size={14} /> New Ticket
                  </button>
                </div>

                {chats.length === 0 && !isCreatingNew ? (
                  <div style={{ textAlign: 'center', padding: '30px 10px', color: 'var(--gray)' }}>
                    <p style={{ fontSize: 13, marginBottom: 10 }}>No support tickets yet.</p>
                    <button
                      onClick={() => setIsCreatingNew(true)}
                      className="btn-outline"
                      style={{ fontSize: 12, padding: '6px 12px' }}
                    >
                      Start New Ticket
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, overflowY: 'auto', maxHeight: 380 }}>
                    {chats.map(chat => {
                      const isSelected = activeChat?._id === chat._id && !isCreatingNew;
                      return (
                        <div
                          key={chat._id}
                          onClick={() => { setActiveChat(chat); setIsCreatingNew(false); }}
                          style={{
                            padding: 12,
                            borderRadius: 10,
                            background: isSelected ? 'white' : 'transparent',
                            border: `1.5px solid ${isSelected ? 'var(--orange)' : 'transparent'}`,
                            cursor: 'pointer',
                            boxShadow: isSelected ? 'var(--shadow)' : 'none',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                            <span style={{ fontWeight: 800, fontSize: 13, color: 'var(--dark)' }}>{chat.issue}</span>
                            <span style={{
                              fontSize: 10,
                              fontWeight: 800,
                              padding: '2px 7px',
                              borderRadius: 12,
                              background: chat.status === 'resolved' ? '#ECFDF5' : '#FFFBEB',
                              color: chat.status === 'resolved' ? 'var(--green)' : '#D97706'
                            }}>
                              {chat.status.toUpperCase()}
                            </span>
                          </div>
                          {chat.orderId && (
                            <div style={{ fontSize: 11, color: 'var(--gray)', fontWeight: 600 }}>
                              Order ID: {chat.orderId}
                            </div>
                          )}
                          <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Clock size={11} /> {new Date(chat.updatedAt).toLocaleDateString()} {new Date(chat.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Right Column: Chat window or New Ticket form */}
              <div style={{ padding: 16, display: 'flex', flexDirection: 'column', background: 'white' }}>
                {isCreatingNew ? (
                  <div>
                    <h3 style={{ fontSize: 16, fontWeight: 900, marginBottom: 14 }}>Create Support Ticket</h3>
                    <form onSubmit={handleCreateTicket}>
                      <div style={{ marginBottom: 12 }}>
                        <label className="form-label">Order ID (Optional)</label>
                        <input
                          className="form-input"
                          placeholder="e.g. ORD-12345 (Leave empty if general issue)"
                          value={orderId}
                          onChange={e => setOrderId(e.target.value)}
                        />
                      </div>

                      <div style={{ marginBottom: 12 }}>
                        <label className="form-label">Issue Category</label>
                        <select
                          className="form-input"
                          value={issue}
                          onChange={e => setIssue(e.target.value)}
                        >
                          {ISSUE_OPTIONS.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </div>

                      <div style={{ marginBottom: 14 }}>
                        <label className="form-label">Issue Description</label>
                        <textarea
                          className="form-input"
                          rows={4}
                          placeholder="Describe your issue or question in detail..."
                          value={description}
                          onChange={e => setDescription(e.target.value)}
                          required
                        />
                      </div>

                      <div style={{ display: 'flex', gap: 10 }}>
                        <button type="submit" className="btn-primary" disabled={submitting}>
                          {submitting ? 'Submitting...' : 'Submit Support Ticket'}
                        </button>
                        {chats.length > 0 && (
                          <button
                            type="button"
                            className="btn-outline"
                            onClick={() => setIsCreatingNew(false)}
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </form>
                  </div>
                ) : activeChat ? (
                  <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {/* Chat Header */}
                    <div style={{ paddingBottom: 12, borderBottom: '1px solid var(--gray-border)', marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                      <div>
                        <div style={{ fontWeight: 800, fontSize: 15 }}>{activeChat.issue}</div>
                        {activeChat.orderId && (
                          <div style={{ fontSize: 12, color: 'var(--orange)', fontWeight: 700 }}>
                            Order ID: {activeChat.orderId}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                          fontSize: 11,
                          fontWeight: 800,
                          padding: '3px 10px',
                          borderRadius: 12,
                          background: activeChat.status === 'resolved' ? '#ECFDF5' : '#FFFBEB',
                          color: activeChat.status === 'resolved' ? 'var(--green)' : '#D97706'
                        }}>
                          {activeChat.status.toUpperCase()}
                        </span>
                        {activeChat.status !== 'resolved' && (
                          <button
                            onClick={handleResolveTicket}
                            style={{ background: '#ECFDF5', color: 'var(--green)', border: '1px solid #A7F3D0', borderRadius: 8, padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                          >
                            <CheckCircle2 size={13} /> Mark Resolved
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Messages Body */}
                    <div ref={messagesContainerRef} style={{ flex: 1, overflowY: 'auto', maxHeight: 280, paddingRight: 6, display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
                      {activeChat.messages?.map((msg, idx) => {
                        const isMe = msg.senderRole === user.role;
                        const isAdmin = msg.senderRole === 'admin';

                        return (
                          <div
                            key={idx}
                            style={{
                              alignSelf: isMe ? 'flex-end' : 'flex-start',
                              maxWidth: '82%',
                              background: isMe ? 'var(--orange)' : isAdmin ? '#EFF6FF' : 'var(--gray-light)',
                              color: isMe ? 'white' : 'var(--dark)',
                              padding: '10px 14px',
                              borderRadius: 14,
                              borderBottomRightRadius: isMe ? 2 : 14,
                              borderBottomLeftRadius: isMe ? 14 : 2,
                              boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                            }}
                          >
                            <div style={{ fontSize: 10, fontWeight: 800, color: isMe ? 'rgba(255,255,255,0.85)' : isAdmin ? '#1D4ED8' : 'var(--gray)', marginBottom: 2 }}>
                              {msg.senderName} ({msg.senderRole.toUpperCase()})
                            </div>
                            <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                              {msg.message}
                            </div>
                            <div style={{ fontSize: 9, textAlign: 'right', marginTop: 4, color: isMe ? 'rgba(255,255,255,0.75)' : 'var(--gray)' }}>
                              {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Reply Input Form */}
                    {activeChat.status === 'resolved' ? (
                      <div style={{ background: '#ECFDF5', color: 'var(--green)', padding: '10px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700, textAlign: 'center' }}>
                        This support ticket is resolved. Click "New Ticket" to open a new query.
                      </div>
                    ) : (
                      <form onSubmit={handleSendMessage} style={{ display: 'flex', gap: 8 }}>
                        <input
                          className="form-input"
                          style={{ flex: 1 }}
                          placeholder="Type your message..."
                          value={replyText}
                          onChange={e => setReplyText(e.target.value)}
                        />
                        <button type="submit" className="btn-primary" style={{ padding: '0 16px' }} disabled={sendingReply}>
                          <Send size={16} />
                        </button>
                      </form>
                    )}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: 40, color: 'var(--gray)' }}>
                    Select a support ticket to view messages or click "New Ticket".
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export default function StaticPage({ type }) {
  const { branding } = useBranding();
  const page = pages[type] || pages.about;
  const Icon = page.icon;
  const activeEmail = branding.supportEmail || 'support@materialstock.in';
  const displayPoints = page.points.map(p => p.startsWith('Email:') ? `Email: ${activeEmail}` : p);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--gray-light)' }}>
      <Navbar />
      <main className="static-page-main page-bottom-gap" style={{ maxWidth: 960, margin: '0 auto', padding: '34px 16px 120px' }}>
        <section style={{ background: 'white', borderRadius: 14, padding: 24, boxShadow: 'var(--shadow)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--orange-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon size={22} color="var(--orange)" />
            </div>
            <div>
              <h1 style={{ fontSize: 26, fontWeight: 900, marginBottom: 3 }}>{page.title}</h1>
              <p style={{ color: 'var(--gray)', fontSize: 14 }}>Material Stock support and information</p>
            </div>
          </div>

          <p style={{ color: 'var(--dark)', fontSize: 15, lineHeight: 1.7, maxWidth: 760 }}>{page.intro}</p>

          <div style={{ display: 'grid', gap: 10, marginTop: 22 }}>
            {displayPoints.map(point => (
              <div key={point} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: 'var(--gray-light)', borderRadius: 10, padding: '12px 14px' }}>
                <ArrowRight size={15} color="var(--orange)" style={{ marginTop: 2, flexShrink: 0 }} />
                <span style={{ fontSize: 14, lineHeight: 1.5 }}>{point}</span>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 24, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Link to="/shops" className="btn-primary" style={{ padding: '10px 16px' }}>Find Shops</Link>
            <Link to="/contact" className="btn-outline" style={{ padding: '8px 16px' }}>Contact Support</Link>
          </div>
        </section>

        {type === 'contact' && <SupportChatSection />}

        {type === 'about' && (
          <section style={{ background: 'white', borderRadius: 14, padding: 24, boxShadow: 'var(--shadow)', marginTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <div style={{ width: 58, height: 58, borderRadius: 14, background: 'linear-gradient(135deg,var(--orange),var(--amber))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 900, fontSize: 22 }}>
                KR
              </div>
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, color: 'var(--orange)', fontSize: 12, fontWeight: 900, textTransform: 'uppercase', marginBottom: 3 }}>
                  <Code2 size={14} /> Developer
                </div>
                <h2 style={{ fontSize: 22, fontWeight: 900, marginBottom: 4 }}>Kanaka Raju</h2>
                <p style={{ color: 'var(--gray)', fontSize: 14, lineHeight: 1.6 }}>
                  Material Stock platform design, development, marketplace features, payments, referrals, and dashboard workflows.
                </p>
              </div>
              <a
                href="https://www.linkedin.com/in/kanakaraju9390/"
                target="_blank"
                rel="noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#0A66C2', color: 'white', borderRadius: 10, padding: '11px 15px', fontWeight: 800, fontSize: 13 }}
              >
                <Linkedin size={16} /> LinkedIn Profile
              </a>
            </div>
          </section>
        )}
        <div style={{ height: 40 }} />
      </main>
    </div>
  );
}
